import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Form as FinalForm, Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import { getMediaUrl } from '../../util/uploadedMedia';
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  FieldTextInput,
  FieldSelect,
  LocationAutocompleteInputField,
  FieldSingleDatePicker,
  ImageFromFile,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  InlineTextButton,
  AspectRatioWrapper,
  IconSpinner,
  Icon,
} from '../../components';

import IconFacebook from './IconFacebook';
import IconInstagram from './IconInstagram';
import IconLinkedIn from './IconLinkedIn';
import css from './OrgProfileWizardModal.module.css';

const ACCEPT_IMAGES = 'image/*';

// Helper to format location value for LocationAutocompleteInputField
// The component expects { search, predictions, selectedPlace } or a string
const formatLocationValue = location => {
  if (!location) return '';
  if (typeof location === 'string') return location;
  // If it's already in the correct format
  if (location.search !== undefined) return location;
  // If it's stored with 'address' property, convert it
  if (location.address) {
    return {
      search: location.address,
      predictions: [],
      selectedPlace: location.selectedPlace || location,
    };
  }
  return '';
};

// Genre options from marketplace config
const GENRE_OPTIONS = [
  { key: 'circus', label: 'Circus' },
  { key: 'play', label: 'Play' },
  { key: 'dance', label: 'Dance' },
  { key: 'singing', label: 'Singing' },
  { key: 'magic', label: 'Magic' },
  { key: 'comedy', label: 'Comedy' },
  { key: 'dj', label: 'DJ' },
  { key: 'cabaret', label: 'Cabaret' },
  { key: 'opera', label: 'Opera' },
  { key: 'ballet', label: 'Ballet' },
  { key: 'cultural-heritage', label: 'Cultural heritage' },
  { key: 'puppetry', label: 'Puppetry' },
  { key: 'musical', label: 'Musical' },
  { key: 'variety', label: 'Variety' },
];

// Year options for founded year
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 1899 }, (_, i) => ({
  key: (currentYear - i).toString(),
  label: (currentYear - i).toString(),
}));

// Role options for team invites
const ROLE_OPTIONS = [
  { key: 'admin', label: 'Admin' },
  { key: 'manager', label: 'Manager' },
  { key: 'creator', label: 'Creator' },
];

// Thumbnail Field for Step 3 - uses control menu like images panel
const ThumbnailField = props => {
  const { form, thumbnail, intl, isMenuOpen, onToggleMenu, onUpload, uploadInProgress } = props;

  // For newly selected files (not yet uploaded)
  const imageFromFile =
    thumbnail && thumbnail.lastModified ? (
      <ImageFromFile
        id={thumbnail.name}
        rootClassName={css.thumbnailImage}
        aspectWidth={4}
        aspectHeight={3}
        file={thumbnail}
      />
    ) : null;

  // For existing images (already uploaded)
  const existingImage =
    thumbnail && thumbnail?.id ? (
      <div className={css.thumbnailImage}>
        <AspectRatioWrapper width={4} height={3}>
          <img className={css.rootForImage} src={thumbnail.link} alt={thumbnail.name} />
        </AspectRatioWrapper>
      </div>
    ) : null;

  const hasImage = imageFromFile || existingImage;

  const handleRemoveThumbnail = () => {
    form.change('showThumbnail', null);
    form.change('showThumbnailImageId', null);
    onToggleMenu(false);
  };

  const controlMenu = hasImage ? (
    <Menu
      rootClassName={css.thumbnailControlMenu}
      isOpen={isMenuOpen}
      onToggleActive={onToggleMenu}
      useArrow={false}
      contentPosition="left"
      contentPlacementOffset={14}
    >
      <MenuLabel className={css.thumbnailMenuLabel}>
        <Icon name="more" />
      </MenuLabel>
      <MenuContent className={css.thumbnailMenuContent}>
        <MenuItem key="change" className={css.thumbnailMenuItem}>
          <InlineTextButton
            className={css.thumbnailMenuItemButton}
            type="button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onToggleMenu(false);
              // Delay to let the menu close and DOM settle before triggering file input
              setTimeout(() => {
                const input = document.getElementById('showThumbnail');
                if (input) {
                  input.value = '';
                  input.click();
                }
              }, 100);
            }}
          >
            <FormattedMessage id="OrgProfileWizardModal.changeThumbnail" />
          </InlineTextButton>
        </MenuItem>
        <MenuItem key="remove" className={css.thumbnailMenuItem}>
          <InlineTextButton
            className={css.thumbnailMenuItemButton}
            type="button"
            onClick={handleRemoveThumbnail}
          >
            <FormattedMessage id="OrgProfileWizardModal.removeThumbnail" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const chooseThumbnailLabel = hasImage ? (
    <div className={css.thumbnailContainer}>
      {imageFromFile}
      {existingImage}
      {controlMenu}
    </div>
  ) : (
    <label className={css.thumbnailPlaceholder} htmlFor="showThumbnail">
      <AspectRatioWrapper width={4} height={3}>
        <span className={css.thumbnailPlaceholderContent}>
          <Icon name="image" size={48} className={css.thumbnailPlaceholderIcon} />
          <span className={css.thumbnailPlaceholderText}>
            <FormattedMessage id="OrgProfileWizardModal.uploadThumbnail" />
          </span>
          <span className={css.thumbnailPlaceholderFormats}>
            <FormattedMessage id="OrgProfileWizardModal.thumbnailFormats" />
          </span>
        </span>
      </AspectRatioWrapper>
    </label>
  );

  return (
    <div className={css.field}>
      <label className={css.fieldLabel}>
        <FormattedMessage id="OrgProfileWizardModal.thumbnailLabel" />
      </label>
      <Field accept={ACCEPT_IMAGES} id="showThumbnail" name="showThumbnail" type="file" form={null}>
        {fieldProps => {
          const { accept, id, input } = fieldProps;
          const { name, type } = input;
          const onChange = async e => {
            const file = e.target.files[0];
            if (file) {
              // Set the file for preview immediately
              form.change('showThumbnail', file);

              // Upload the image to get the UUID
              if (onUpload) {
                try {
                  await onUpload({
                    id: `show-thumbnail-${Date.now()}`,
                    file: file,
                  });
                } catch (error) {
                  console.error('Error uploading thumbnail:', error);
                }
              }
            }
          };

          return (
            <div className={css.thumbnailWrapper}>
              {chooseThumbnailLabel}
              {uploadInProgress && (
                <div className={css.thumbnailUploadingOverlay}>
                  <IconSpinner className={css.thumbnailSpinner} />
                </div>
              )}
              <input
                accept={accept}
                id={id}
                name={name}
                className={css.thumbnailInput}
                onChange={onChange}
                type={type}
              />
            </div>
          );
        }}
      </Field>
    </div>
  );
};

const ProfileImageField = props => {
  const { form, profileImage, intl } = props;

  const imageFromFile =
    profileImage && profileImage.lastModified ? (
      <ImageFromFile
        id={profileImage.name}
        rootClassName={css.uploadedImage}
        aspectRatioClassName={css.squareAspectRatio}
        file={profileImage}
      />
    ) : null;

  const avatarComponent =
    profileImage && profileImage?.id ? (
      <div className={css.uploadedImage}>
        <div className={css.squareAspectRatio}>
          <img
            className={css.rootForImage}
            src={getMediaUrl(profileImage, 'card')}
            alt={profileImage.name}
          />
        </div>
      </div>
    ) : null;

  const hasImage = imageFromFile || avatarComponent;

  const chooseAvatarLabel = hasImage ? (
    <div className={css.avatarContainer}>
      {imageFromFile}
      {avatarComponent}
      <div className={css.changeAvatar}>
        <Icon name="edit" size={16} className={css.changeAvatarIcon} />
        <FormattedMessage id="ProfileImageForm.changeAvatar" />
      </div>
    </div>
  ) : (
    <div className={css.avatarPlaceholder}>
      <div className={css.avatarPlaceholderText}>
        <FormattedMessage id="ProfileImageForm.addYourProfilePicture" />
      </div>
      <div className={css.avatarPlaceholderTextMobile}>
        <FormattedMessage id="ProfileImageForm.addYourProfilePictureMobile" />
      </div>
    </div>
  );

  return (
    <div className={css.field}>
      <label className={css.fieldLabel}>
        <FormattedMessage id="OrgProfileWizardModal.profilePhotoLabel" />
      </label>
      <Field accept={ACCEPT_IMAGES} id="profileImage" name="profileImage" type="file" form={null}>
        {fieldProps => {
          const { accept, id, input } = fieldProps;
          const { name, type } = input;
          const onChange = e => {
            const file = e.target.files[0];
            if (file) {
              form.change('profileImage', file);
            }
          };

          const triggerFileInput = () => {
            const input = document.getElementById(id);
            if (input) {
              input.value = '';
              input.click();
            }
          };

          return (
            <div className={css.profileImageWrapper}>
              <div
                className={css.profileImageLabel}
                onClick={triggerFileInput}
                role="button"
                tabIndex={0}
              >
                {chooseAvatarLabel}
              </div>
              <input
                accept={accept}
                id={id}
                name={name}
                className={css.profileImageInput}
                onChange={onChange}
                type={type}
              />
            </div>
          );
        }}
      </Field>
    </div>
  );
};

const SocialLinkField = props => {
  const { name, placeholder, icon, intl } = props;

  return (
    <div className={css.socialLinkField}>
      <FieldTextInput
        className={css.socialInput}
        type="text"
        id={name}
        name={name}
        placeholder={placeholder}
      />
      <span className={css.socialIcon}>{icon}</span>
    </div>
  );
};

// Invite row component for Step 2
const InviteRowField = props => {
  const { fieldName, index, intl, onRemove, showRemoveButton, currentUserEmail } = props;

  const emailPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.inviteEmailPlaceholder',
  });

  // Validation
  const emailRequiredMessage = intl.formatMessage({
    id: 'OrgProfileWizardModal.emailRequired',
  });
  const emailInvalidMessage = intl.formatMessage({
    id: 'OrgProfileWizardModal.emailInvalid',
  });
  const cannotInviteSelfMessage = intl.formatMessage({
    id: 'OrgProfileWizardModal.cannotInviteSelf',
  });

  // Compose validators - email is optional but must be valid if provided
  const emailValidation = value => {
    if (!value || value.trim() === '') {
      return undefined; // Empty is OK - we filter these out on submit
    }
    const emailFormatError = validators.emailFormatValid(emailInvalidMessage)(value);
    if (emailFormatError) return emailFormatError;

    // Check if trying to invite self
    if (currentUserEmail && value.toLowerCase().trim() === currentUserEmail.toLowerCase()) {
      return cannotInviteSelfMessage;
    }
    return undefined;
  };

  return (
    <div className={css.inviteRow}>
      <div className={css.inviteEmailField}>
        <FieldTextInput
          type="email"
          id={`${fieldName}.email`}
          name={`${fieldName}.email`}
          placeholder={emailPlaceholder}
          validate={emailValidation}
        />
      </div>
      <div className={css.inviteRoleField}>
        <FieldSelect id={`${fieldName}.role`} name={`${fieldName}.role`} options={ROLE_OPTIONS} />
      </div>
      {showRemoveButton && (
        <button type="button" className={css.removeInviteButton} onClick={() => onRemove(index)}>
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
};

// Step 2: Invite Your Team
const Step2InviteTeam = props => {
  const { intl, form, values, currentUserEmail } = props;

  return (
    <div className={css.formFields}>
      <p className={css.stepDescription}>
        <FormattedMessage id="OrgProfileWizardModal.inviteTeamDescription" />
      </p>

      <div className={css.field}>
        <label className={css.fieldLabel}>
          <FormattedMessage id="OrgProfileWizardModal.invitesLabel" />
        </label>
        <FieldArray name="teamInvites">
          {({ fields }) => (
            <div className={css.inviteRows}>
              {fields.map((name, index) => (
                <InviteRowField
                  key={name}
                  fieldName={name}
                  index={index}
                  intl={intl}
                  onRemove={() => fields.remove(index)}
                  showRemoveButton={fields.length > 1}
                  currentUserEmail={currentUserEmail}
                />
              ))}
            </div>
          )}
        </FieldArray>

        <button
          type="button"
          className={css.addInviteButton}
          onClick={() => form.mutators.push('teamInvites', { email: '', role: 'creator' })}
        >
          <FormattedMessage id="OrgProfileWizardModal.addNewInvite" />
        </button>
      </div>
    </div>
  );
};

// Step 3: Create Your First Show
const Step3CreateShow = props => {
  const {
    intl,
    form,
    values,
    thumbnailMenuOpen,
    onToggleThumbnailMenu,
    onThumbnailUpload,
    thumbnailUploadInProgress,
  } = props;

  const showTitlePlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.pageTitlePlaceholder',
  });
  const venuePlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.venuePlaceholder',
  });
  const aboutShowPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.aboutPagePlaceholder',
  });

  // Validation
  const showTitleRequiredMessage = intl.formatMessage({
    id: 'OrgProfileWizardModal.pageTitleRequired',
  });
  const showTitleRequired = validators.required(showTitleRequiredMessage);

  return (
    <div className={css.formFields}>
      <p className={css.stepDescription}>
        <FormattedMessage id="OrgProfileWizardModal.createPageDescription" />
      </p>

      {/* Thumbnail */}
      <ThumbnailField
        form={form}
        thumbnail={values.showThumbnail}
        intl={intl}
        isMenuOpen={thumbnailMenuOpen}
        onToggleMenu={onToggleThumbnailMenu}
        onUpload={onThumbnailUpload}
        uploadInProgress={thumbnailUploadInProgress}
      />

      {/* Show Title */}
      <FieldTextInput
        className={css.field}
        type="text"
        id="showTitle"
        name="showTitle"
        label={intl.formatMessage({ id: 'OrgProfileWizardModal.pageTitleLabel' })}
        placeholder={showTitlePlaceholder}
        validate={showTitleRequired}
      />

      {/* Venue */}
      <div className={css.field}>
        <label className={css.fieldLabel}>
          <FormattedMessage id="OrgProfileWizardModal.venueLabel" />
        </label>
        <LocationAutocompleteInputField
          className={css.locationField}
          inputClassName={css.locationInput}
          iconClassName={css.locationIcon}
          predictionsClassName={css.locationPredictions}
          name="showVenue"
          placeholder={venuePlaceholder}
          useDefaultPredictions={false}
          format={v => v}
          valueFromForm={formatLocationValue(values.showVenue)}
        />
      </div>

      {/* Dates */}
      <div className={css.datesRow}>
        <div className={css.dateFieldWrapper}>
          <label className={css.fieldLabel}>
            <FormattedMessage id="OrgProfileWizardModal.startDateLabel" />
          </label>
          <FieldSingleDatePicker
            className={css.dateField}
            id="showStartDate"
            name="showStartDate"
            placeholderText={intl.formatMessage({
              id: 'OrgProfileWizardModal.startDatePlaceholder',
            })}
            useMobileMargins={false}
            showErrorMessage={false}
            isOutsideRange={() => false}
          />
        </div>
        <div className={css.dateFieldWrapper}>
          <label className={css.fieldLabel}>
            <FormattedMessage id="OrgProfileWizardModal.endDateLabel" />
          </label>
          <FieldSingleDatePicker
            className={css.dateField}
            id="showEndDate"
            name="showEndDate"
            placeholderText={intl.formatMessage({ id: 'OrgProfileWizardModal.endDatePlaceholder' })}
            useMobileMargins={false}
            showErrorMessage={false}
            isOutsideRange={day => {
              const startDate = values.showStartDate?.date;
              if (!startDate) return false;
              return moment(day).isBefore(moment(startDate), 'day');
            }}
          />
        </div>
      </div>

      {/* About this show */}
      <FieldTextInput
        className={css.field}
        type="textarea"
        id="showDescription"
        name="showDescription"
        label={intl.formatMessage({ id: 'OrgProfileWizardModal.aboutPageLabel' })}
        placeholder={aboutShowPlaceholder}
      />
    </div>
  );
};

const OrgProfileWizardModalComponent = props => {
  const {
    id,
    intl,
    isOpen,
    onClose,
    onSubmit,
    onBack,
    onSkip,
    onManageDisableScrolling,
    submitInProgress,
    submitError,
    initialValues,
    currentStep,
    totalSteps,
    currentUserEmail,
    onThumbnailUpload,
    thumbnailUploadInProgress,
  } = props;

  const [thumbnailMenuOpen, setThumbnailMenuOpen] = useState(false);

  const step = currentStep || 1;
  const total = totalSteps || 3;
  const progressPercentage = (step / total) * 100;

  // Validation messages
  const nameRequiredMessage = intl.formatMessage({
    id: 'OrgProfileWizardModal.nameRequired',
  });
  const nameRequired = validators.required(nameRequiredMessage);

  // Placeholders
  const namePlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.namePlaceholder',
  });
  const websitePlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.websitePlaceholder',
  });
  const genresPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.genresPlaceholder',
  });
  const foundedPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.foundedPlaceholder',
  });
  const locationPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.locationPlaceholder',
  });
  const bioPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.bioPlaceholder',
  });
  const facebookPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.facebookPlaceholder',
  });
  const instagramPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.instagramPlaceholder',
  });
  const linkedinPlaceholder = intl.formatMessage({
    id: 'OrgProfileWizardModal.linkedinPlaceholder',
  });

  return (
    <Modal
      id={id}
      className={css.modalWrapper}
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <FinalForm
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
          teamInvites: initialValues.teamInvites || [{ email: '', role: 'creator' }],
        }}
        keepDirtyOnReinitialize={true}
        mutators={{
          ...arrayMutators,
        }}
        render={formRenderProps => {
          const { handleSubmit, form, values, submitting, pristine, errors } = formRenderProps;
          const profileImage = values.profileImage;

          // Step-specific validation
          const isStep1Invalid = step === 1 && (!values.name || values.name.trim() === '');
          const isStep3Invalid =
            step === 3 && (!values.showTitle || values.showTitle.trim() === '');
          const hasStepValidationError = isStep1Invalid || isStep3Invalid;

          const submitDisabled = submitting || submitInProgress || hasStepValidationError;

          // Skip button logic:
          // - Step 1: No skip (required - must set org profile)
          // - Step 2: Show skip (optional - team invites can be skipped)
          // - Step 3: Show skip (optional - creating first show can be skipped)
          const showSkipButton = onSkip && (step === 2 || step === 3);

          // Get step-specific title
          const getStepTitle = () => {
            switch (step) {
              case 1:
                return intl.formatMessage({ id: 'OrgProfileWizardModal.title' });
              case 2:
                return intl.formatMessage({ id: 'OrgProfileWizardModal.inviteTeamTitle' });
              case 3:
                return intl.formatMessage({ id: 'OrgProfileWizardModal.createPageTitle' });
              default:
                return intl.formatMessage({ id: 'OrgProfileWizardModal.title' });
            }
          };

          // Render Step 1 content - Organization Profile
          const renderStep1Content = () => (
            <div className={css.formFields}>
              {/* Profile Photo */}
              <ProfileImageField form={form} profileImage={profileImage} intl={intl} />

              {/* Organization Name */}
              <FieldTextInput
                className={css.field}
                type="text"
                id="name"
                name="name"
                label={intl.formatMessage({ id: 'OrgProfileWizardModal.nameLabel' })}
                placeholder={namePlaceholder}
                validate={nameRequired}
              />

              {/* Website */}
              <FieldTextInput
                className={css.field}
                type="text"
                id="website"
                name="website"
                label={intl.formatMessage({ id: 'OrgProfileWizardModal.websiteLabel' })}
                placeholder={websitePlaceholder}
              />

              {/* Genres */}
              <FieldSelect
                className={css.field}
                id="genres"
                name="genres"
                label={intl.formatMessage({ id: 'OrgProfileWizardModal.genresLabel' })}
                options={GENRE_OPTIONS}
                placeholder={genresPlaceholder}
              />

              {/* Founded Year */}
              <FieldSelect
                className={css.field}
                id="founded"
                name="founded"
                label={intl.formatMessage({ id: 'OrgProfileWizardModal.foundedLabel' })}
                options={YEAR_OPTIONS}
                placeholder={foundedPlaceholder}
              />

              {/* Location */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  <FormattedMessage id="OrgProfileWizardModal.locationLabel" />
                </label>
                <LocationAutocompleteInputField
                  className={css.locationField}
                  inputClassName={css.locationInput}
                  iconClassName={css.locationIcon}
                  predictionsClassName={css.locationPredictions}
                  name="location"
                  placeholder={locationPlaceholder}
                  useDefaultPredictions={false}
                  format={v => v}
                  valueFromForm={formatLocationValue(values.location)}
                />
              </div>

              {/* Bio */}
              <FieldTextInput
                className={css.field}
                type="textarea"
                id="bio"
                name="bio"
                label={intl.formatMessage({ id: 'OrgProfileWizardModal.bioLabel' })}
                placeholder={bioPlaceholder}
              />

              {/* Social Links */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  <FormattedMessage id="OrgProfileWizardModal.socialLinksLabel" />
                </label>
                <div className={css.socialLinks}>
                  <SocialLinkField
                    name="facebookLink"
                    placeholder={facebookPlaceholder}
                    icon={<IconFacebook />}
                    intl={intl}
                  />
                  <SocialLinkField
                    name="instagramLink"
                    placeholder={instagramPlaceholder}
                    icon={<IconInstagram />}
                    intl={intl}
                  />
                  <SocialLinkField
                    name="linkedinLink"
                    placeholder={linkedinPlaceholder}
                    icon={<IconLinkedIn />}
                    intl={intl}
                  />
                </div>
              </div>
            </div>
          );

          // Render step content based on current step
          const renderStepContent = () => {
            switch (step) {
              case 1:
                return renderStep1Content();
              case 2:
                return (
                  <Step2InviteTeam
                    intl={intl}
                    form={form}
                    values={values}
                    currentUserEmail={currentUserEmail}
                  />
                );
              case 3:
                return (
                  <Step3CreateShow
                    intl={intl}
                    form={form}
                    values={values}
                    thumbnailMenuOpen={thumbnailMenuOpen}
                    onToggleThumbnailMenu={setThumbnailMenuOpen}
                    onThumbnailUpload={onThumbnailUpload}
                    thumbnailUploadInProgress={thumbnailUploadInProgress}
                  />
                );
              default:
                return renderStep1Content();
            }
          };

          // Get button text based on step
          const getSubmitButtonText = () => {
            if (step === total) {
              return intl.formatMessage({ id: 'OrgProfileWizardModal.finishButton' });
            }
            return intl.formatMessage({ id: 'OrgProfileWizardModal.nextButton' });
          };

          return (
            <form onSubmit={handleSubmit} className={css.form}>
              <div className={css.content}>
                {/* Progress bar */}
                <div className={css.stepHeader}>
                  <div className={css.progressBar}>
                    <div
                      className={css.progressLine}
                      style={{ '--progress': `${progressPercentage}%` }}
                    />
                  </div>
                  <span className={css.stepText}>
                    <FormattedMessage id="OrgProfileWizardModal.stepOf" values={{ step, total }} />
                  </span>
                </div>

                {/* Title */}
                <h2 className={css.title}>{getStepTitle()}</h2>

                {/* Step content */}
                {renderStepContent()}
              </div>

              {/* Footer */}
              <div className={css.footer}>
                {showSkipButton && (
                  <InlineTextButton className={css.skipButton} type="button" onClick={onSkip}>
                    <FormattedMessage id="OrgProfileWizardModal.skipButton" />
                  </InlineTextButton>
                )}
                <div className={css.footerButtons}>
                  {step > 1 && onBack && (
                    <SecondaryButton className={css.backButton} type="button" onClick={onBack}>
                      <FormattedMessage id="OrgProfileWizardModal.backButton" />
                    </SecondaryButton>
                  )}
                  <PrimaryButton
                    className={css.submitButton}
                    inProgressClassName={css.submitButtonInProgress}
                    type="submit"
                    inProgress={submitting || submitInProgress}
                    disabled={submitDisabled}
                  >
                    {getSubmitButtonText()}
                  </PrimaryButton>
                </div>
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
};

OrgProfileWizardModalComponent.defaultProps = {
  isOpen: false,
  submitInProgress: false,
  submitError: null,
  initialValues: {},
  currentStep: 1,
  totalSteps: 3,
  onBack: null,
  onSkip: null,
  currentUserEmail: null,
};

OrgProfileWizardModalComponent.propTypes = {
  id: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onSkip: PropTypes.func,
  onManageDisableScrolling: PropTypes.func.isRequired,
  submitInProgress: PropTypes.bool,
  submitError: PropTypes.object,
  initialValues: PropTypes.object,
  currentStep: PropTypes.number,
  totalSteps: PropTypes.number,
  currentUserEmail: PropTypes.string,
};

const OrgProfileWizardModal = injectIntl(OrgProfileWizardModalComponent);

export default OrgProfileWizardModal;
